import { makeAutoObservable } from 'mobx';
import { Candidate, CandidateAttributeOption, CandidateKey, CandidatePhoto, Region } from '~/definitions/candidates';
import renderFieldByType from './fn/renderFieldByType';
import { DEFAULT_CANDIDATE, DEFAULT_REGIONS } from '~/consts/candidates';
import { getCandidateListByRegionId } from './fn/getCandidateListByRegionId';
import getSelectedCandidate from './fn/getSelectedCandidate';
import getSelectedRegion from './fn/getSelectedRegion';
import deleteCandidate from './fn/deleteCandidate';
import setCandidateById from './fn/setCandidateById';
import cancelEdit from './fn/cancelEdit';
import onFieldValueChange from './fn/onFieldValueChange';
import resetDetailForm from './fn/resetDetailForm';
import fetchCandidatePhoto from './fn/fetchCandidatePhoto';
import onSelectCandidate from './fn/onSelectCandidate';
import getCandidatePhoto from './fn/getCandidatePhoto';
import { createCandidate } from './fn/createCandidate';
import { updateCandidate } from './fn/updateCandidate';
import { validateCandidateForm } from './fn/validateCandidateForm';
import { showMessageModalWithFieldKeys } from './fn/showMessageModalWithFieldKeys';
import { getCandidateAttributeOptions } from './fn/getCandidateAttributeOptions';
import { getIsLoading } from './fn/getIsLoading';
import copyCandidate from './fn/copyCandidate';
import onChangeRegionForCopy from './fn/onChangeRegionForCopy';
import onCreateCandidate from './fn/onCreateCandidate';
import updateCandidatePhoto from './fn/updateCandidatePhoto';
import { unlinkCandidateFromRegion } from './fn/unlinkCandidateFromRegion';
import { unlinkMedia } from './fn/unlinkMedia';
import { getCandidateRowFields } from './fn/getCandidateRowFields';
import sortCandidates from '../../serverUtils/sortCandidates';

export const DEFAULT_CANDIDATE_PHOTO: CandidatePhoto = {
    name: '',
    url: '',
};

export default class CandidateStore {
    candidates: Candidate[] = [];
    candidatePhoto: CandidatePhoto = DEFAULT_CANDIDATE_PHOTO;
    selectedCandidateId: Candidate['id'] = '';
    selectedCandidateMemo: Candidate | undefined;
    selectedCandidateEdit: Candidate = { ...DEFAULT_CANDIDATE };
    regions: Region[] = DEFAULT_REGIONS;
    selectedRegionId: string = '';
    isCreateMode: boolean = false;
    isEditMode: boolean = false;
    selectedRegionIdForCopy: string = '';

    candidateGenderOptions: CandidateAttributeOption[] = [];

    isLoadingCandidateList: boolean = false;
    isDeletingCandidate: boolean = false;
    isFetchingCandidatePhoto: boolean = false;
    isUploadingCandidatePhoto: boolean = false;
    previewCandidatePhoto: File | null = null;
    isCreatingCandidate: boolean = false;
    isUpdatingCandidate: boolean = false;
    isLoadingCandidateAttributesOptions: boolean = false;

    fieldsKeyWithIssues: CandidateKey[] = [];

    isCopyingCandidate: boolean = false;
    isCopyingCandidateSuccess: boolean = false;

    constructor() {
        this.resetToDefault();
        makeAutoObservable(this);
    }

    resetToDefault() {
        this.candidates = [];
        this.candidatePhoto = DEFAULT_CANDIDATE_PHOTO;
        this.selectedCandidateId = '';
        this.selectedCandidateMemo = undefined;
        this.selectedCandidateEdit = { ...DEFAULT_CANDIDATE };
        this.regions = DEFAULT_REGIONS;
        this.selectedRegionId = '';
        this.isCreateMode = false;
        this.isEditMode = false;
        this.selectedRegionIdForCopy = '';

        this.candidateGenderOptions = [];

        this.isLoadingCandidateList = false;
        this.isDeletingCandidate = false;
        this.isFetchingCandidatePhoto = false;
        this.isUploadingCandidatePhoto = false;
        this.previewCandidatePhoto = null;
        this.isCreatingCandidate = false;
        this.isUpdatingCandidate = false;
        this.isLoadingCandidateAttributesOptions = false;
        this.isCopyingCandidate = false;
        this.isCopyingCandidateSuccess = false;

        this.fieldsKeyWithIssues = [];
    }

    setCandidates = (candidates: typeof this.candidates) => {
        this.candidates = candidates;
    };

    setSelectedCandidateId = (number: typeof this.selectedCandidateId) => {
        this.selectedCandidateId = number;
    };

    setRegions = (regions: typeof this.regions) => {
        this.regions = regions;
    };

    setSelectedRegionId = (id: typeof this.selectedRegionId) => {
        this.selectedRegionId = id;
    };

    setIsCreateMode = (isCreateMode: typeof this.isCreateMode) => {
        this.isCreateMode = isCreateMode;
    };

    setIsEditMode = (isEditMode: boolean) => {
        this.isEditMode = isEditMode;
    };

    setSelectedRegionIdForCopy = (id: typeof this.selectedRegionIdForCopy) => {
        this.selectedRegionIdForCopy = id;
    };

    setCandidateGenderOptions = (optinos: CandidateAttributeOption[]) => {
        this.candidateGenderOptions = optinos;
    };

    setIsLoadingCandidateList = (isLoading: boolean) => {
        this.isLoadingCandidateList = isLoading;
    };

    setIsCreatingCandidate = (isCreating: boolean) => {
        this.isCreatingCandidate = isCreating;
    };

    setIsUpdatingCandidate = (isUpdating: boolean) => {
        this.isUpdatingCandidate = isUpdating;
    };

    setIsLoadingCandidateAttributesOptions = (isLoading: boolean) => {
        this.isLoadingCandidateAttributesOptions = isLoading;
    };

    setSelectedCandidateMemo = (candidate?: Candidate) => {
        this.selectedCandidateMemo = candidate;
    };

    setSelectedCandidateEdit = (candidate: Candidate) => {
        this.selectedCandidateEdit = candidate;
    };

    setDeletingCandidate = (isDeleting: boolean) => {
        this.isDeletingCandidate = isDeleting;
    };

    setCandidatePhoto = (candidatePhoto: CandidatePhoto) => {
        this.candidatePhoto = candidatePhoto;
    };

    setIsFetchingCandidatePhoto = (isFetching: boolean) => {
        this.isFetchingCandidatePhoto = isFetching;
    };

    setIsUploadingCandidatePhoto = (isUploading: boolean) => {
        this.isUploadingCandidatePhoto = isUploading;
    };

    setPreviewCandidatePhoto = (file: File | null) => {
        this.previewCandidatePhoto = file;
    };

    setFieldsKeyWithIssues = (keys: CandidateKey[]) => {
        this.fieldsKeyWithIssues = keys;
    };

    setCopyingCandidate = (isCopying: boolean) => {
        this.isCopyingCandidate = isCopying;
    };

    setIsCopyingCandidateSuccess = (isCopyingSuccess: boolean) => {
        this.isCopyingCandidateSuccess = isCopyingSuccess;
    };

    renderFieldByType = renderFieldByType;
    getCandidateAttributeOptions = getCandidateAttributeOptions;
    getCandidateRowFields = getCandidateRowFields;

    getCandidateListByRegionId = getCandidateListByRegionId;
    getSelectedCandidate = getSelectedCandidate;
    getSelectedRegion = getSelectedRegion;
    deleteCandidate = deleteCandidate;
    cancelEdit = cancelEdit;
    setCandidateById = setCandidateById;
    onFieldValueChange = onFieldValueChange;
    resetDetailForm = resetDetailForm;
    fetchCandidatePhoto = fetchCandidatePhoto;
    onSelectCandidate = onSelectCandidate;
    getCandidatePhoto = getCandidatePhoto;
    createCandidate = createCandidate;
    updateCandidate = updateCandidate;
    validateCandidateForm = validateCandidateForm;
    showMessageModalWithFieldKeys = showMessageModalWithFieldKeys;

    onCreateCandidate = onCreateCandidate;
    updateCandidatePhoto = updateCandidatePhoto;
    getIsLoading = getIsLoading;

    copyCandidate = copyCandidate;
    onChangeRegionForCopy = onChangeRegionForCopy;
    unlinkCandidateFromRegion = unlinkCandidateFromRegion;
    unlinkMedia = unlinkMedia;
    sortCandidates = sortCandidates;
}
